"use client";

import { useTranslation } from "@/app/i18n/client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "../../../ui/input";
import { cn } from "@/lib/utils";
import LocaleLink from "@/components/locale-link";
import { Button } from "../../../ui/button";
import * as actions from "@/actions";
import { useEffect, useState } from "react";
import { SignInEmailValidatorSchema, signinEmailValidatorSchema } from "@/lib/validators/signin-email";
import OTPFormSignup from "../otp-form-signup";
import { CallAPI_SignIn } from "@/util/services/auth/api";
import { sendOTPSignup } from "@/actions";
import DividerHorizontal from "@/components/divider-horizontal";
import { CallAPI_UrlAuthFB, CallAPI_UrlAuthGoogle } from "@/util/services/auth/api";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { Loader2 } from "lucide-react";
import { useContext } from "react";
interface SignInFormProps {
    children?: React.ReactNode;
    guestId?: string;
    sso?: string;
    config?: any;
    isFailed?: () => void;
}

const SignInNewForm: React.FC<SignInFormProps> = ({ children, guestId, sso, config, isFailed }) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [otpVisible, setOtpVisible] = useState<boolean>(false);
    const [isRefCode, setIsRefCode] = useState<string>("");
    const [isRef, setIsRef] = useState<string>("");
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [isEmail, setEmail] = useState<string>("");
    const [isPassword, setPassword] = useState<string>("");
    const [urlAuthFB, setUrlAuthFB] = useState<string>("");
    const [urlAuthGoogle, setUrlAuthGoogle] = useState<string>("");
    const systemConfig = config;
    const colorStyle = useContext(ColorStyleContext);

    useEffect(() => {
        const fetchUrl = async () => {
            const urlAuthFB = await CallAPI_UrlAuthFB();
            setUrlAuthFB(urlAuthFB.result);
            const urlAuthGoogle = await CallAPI_UrlAuthGoogle();
            setUrlAuthGoogle(urlAuthGoogle.result);
        };
        fetchUrl();
    }, []);

    const form = useForm<SignInEmailValidatorSchema>({
        resolver: zodResolver(signinEmailValidatorSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = async (values: SignInEmailValidatorSchema) => {
        setLoading(true);
        const response = await CallAPI_SignIn({
            username: values.email,
            password: values.password,
            authFlow: "email",
        });
        if (systemConfig?.enableVerifyEmail) {
            if (response.result) {
                if (response.result.isVerify === false) {
                    setEmail(values.email);
                    setPassword(values.password);
                    const responseOTP = await sendOTPSignup(values.email);
                    if (responseOTP.result.success) {
                        setIsRefCode(responseOTP.result.refCode);
                        setIsRef(responseOTP.result.ref);
                        setOtpVisible(true);
                        setLoading(false);
                        return; // Exit early if showing OTP
                    }
                }
            } else {
                if (isFailed) isFailed();
            }
        }

        if (response.result && (response.result.isVerify === true || !systemConfig?.enableVerifyEmail)) {
            actions
                .signIn(values.email, values.password)
                .catch((error: any) => {
                    console.log("error", error);

                    if (isFailed) isFailed();
                    // toast.error(`${t("login.toast_failed_message")}`, {
                    //     position: "bottom-center",
                    //     duration: 3000
                    // });
                })
                .finally(() => {
                    // setLoading(false);
                    setTimeout(() => {
                        setLoading(false);
                    }, 3000);
                });
        } else {
            if (isFailed){ 
                isFailed();
            } else {
                setLoading(false);
            }
        }
    };

    return (
        <>
            {otpVisible ? (
                <OTPFormSignup
                    type="email"
                    value={form.getValues("email")}
                    numDigits={6}
                    data={{
                        ref: isRef,
                        transId: isRef,
                        refCode: isRefCode,
                    }}
                    onOTPSubmit={(isSuccess) => {
                        if (isSuccess) {
                            onSubmit({ email: isEmail, password: isPassword });
                        }
                    }}
                />
            ) : (
                <>
                    <div className="flex items-center justify-center">
                        <span className="font-normal text-xl" style={{ color: colorStyle?.headingSub || "#2c568a"}}>{t("login.login_with_password_tab")}</span>
                    </div>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                            <FormField
                                control={form.control}
                                // name="username"
                                name="email"
                                render={({ field, formState }) => (
                                    <FormItem>
                                        <FormLabel
                                            className="form-input-label"
                                            style={{
                                                color: colorStyle?.text || "#3d3d3d",
                                            }}>
                                            {t("login.login_input_id")}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t("login.login_input_id")}
                                                {...field}
                                                value={field.value || ""}
                                                className={cn(
                                                    "form-input",
                                                    formState.errors.email && "form-input-error",
                                                )}
                                            />
                                        </FormControl>
                                        <FormMessage translate="yes" />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field, formState }) => (
                                    <FormItem>
                                        <div className="flex justify-between">
                                            <FormLabel
                                                className="form-input-label"
                                                style={{
                                                    color: colorStyle?.text || "#3d3d3d",
                                                }}>
                                                {t("login.login_input_password")}
                                            </FormLabel>
                                            <LocaleLink
                                                href="/forgot-password"
                                                className="text-lg font-normal dynamic-color"
                                                style={
                                                    {
                                                        "--dynamic-color": colorStyle?.link || "#009eca",
                                                        "--dynamic-hover-color": colorStyle?.linkHover || "#254b7c",
                                                        "--dynamic-focus-color": colorStyle?.linkHover || "#254b7c",
                                                    } as React.CSSProperties
                                                }>
                                                {t("login.login_btn_forgot")}
                                            </LocaleLink>
                                        </div>
                                        <FormControl>
                                            <Input
                                                type="password"
                                                placeholder={t("login.login_input_password")}
                                                {...field}
                                                value={field.value || ""}
                                                className={cn(
                                                    "form-input",
                                                    formState.errors.password && "form-input-error",
                                                )}
                                            />
                                        </FormControl>
                                        <FormMessage translate="yes" />
                                    </FormItem>
                                )}
                            />
                            <div className="w-full flex flex-col items-center">
                                <button
                                    className={`w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color rounded-full flex items-center justify-center text-lg font-bold ${isLoading ? "disabled cursor-not-allowed opacity-50" : ""}`}
                                    type="submit"
                                    style={
                                        {
                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                        } as React.CSSProperties
                                    }>
                                    <div className="flex items-center justify-center gap-2">
                                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.login_btn_login")}
                                    </div>
                                </button>
                            </div>
                        </form>
                    </Form>
                    {(systemConfig?.enableFacebookAuth || systemConfig?.enableGoogleAuth) && (
                        <div className="flex flex-col px-6 pb-6">
                            <div className="flex flex-row gap-4">
                                <DividerHorizontal />
                                <h4
                                    className="grow font-normal text-sm w-fit text-center content-center whitespace-nowrap"
                                    style={{
                                        color: colorStyle?.text || "#3d3d3d",
                                    }}>
                                    {t("login.or_login_with")}
                                </h4>
                                <DividerHorizontal />
                            </div>

                            <div className="flex flex-col sm:flex-col px-6 gap-5">
                                {systemConfig?.enableGoogleAuth && (
                                    <a className="w-full" href={urlAuthGoogle}>
                                        <button
                                            type="submit"
                                            className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                            style={{
                                                "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                                } as React.CSSProperties
                                            }>
                                            <img
                                                src="https://www.svgrepo.com/show/475656/google-color.svg"
                                                alt="Google"
                                                className="h-[18px] w-[18px]"
                                            />
                                            {t("login.sign_in_with_google")}
                                        </button>
                                    </a>
                                )}
                                {systemConfig?.enableFacebookAuth && (
                                    <a className="w-full" href={urlAuthFB}>
                                        <button
                                            type="submit"
                                            className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                            style={{
                                                "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                                "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                                "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                                } as React.CSSProperties
                                            }>
                                            <img
                                                src="https://www.svgrepo.com/show/111203/facebook.svg"
                                                alt="Facebook"
                                                className="h-[18px] w-[18px]"
                                            />
                                            {t("login.sign_in_with_facebook")}
                                        </button>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default SignInNewForm;
